import React, { forwardRef, useState, useEffect, useImperativeHandle, useCallback } from 'react';
import { Grid, TextField, Typography, Select, MenuItem, FormControl, InputLabel, FormHelperText, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Paper, Tooltip, IconButton, Box } from '@mui/material';
import { useErrorInputs } from '../../hooks/useErrorInputs';
import { getCountries, getStatesAndCities } from '../../services/sharedControls.service';
import { useDataAuth } from '../../hooks/useDataAuth';
import { AddTask, Delete, Edit } from '@mui/icons-material';
import { useNotification } from '../../hooks/useNotification';
import { deleteOffice, updateOffice } from '../../services/proveedor.service';
import { useTranslation } from 'react-i18next';

export const LocationForm = forwardRef(({ dto, handleDirectInputChange, isEdit }, ref) => {
    const { t } = useTranslation();
    const initialState = {
        id_supplier_office: '',
        type_office: 'Oficina Principal',
        address: '',
        phone: '',
        cellphone: '',
        email: '',
        postal_code: '',
        country: 'CO',
        state: '',
        city: '',
        edit: false
    }

    const [locationForm, setLocationForm] = useState(initialState);
    const [errorInputs, handleErrorInput] = useErrorInputs({});
    const [{ userSession }] = useDataAuth();

    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [idxEdit, setIdxEdit] = useState(null);
    const [sendNotification] = useNotification();

    const fetchData = useCallback(async () => {
        try {
            const response = await getCountries(userSession.token);
            if (response.data)
                setCountries(response.data);
        } catch (err) {
            console.log(err.message);
        }
    }, [userSession.token]);

    const GetStatesAndCities = useCallback(async (action, variables) => {
        try {
            const response = await getStatesAndCities(userSession.token, action, variables);
            if (response.data) {
                if (action === 'STATE')
                    setStates(response.data);
                else {
                    setCities(response.data);
                }
            }
        } catch (err) {
            console.log(err.message);
        }
    }, [userSession.token]);

    useEffect(() => {
        fetchData();
        GetStatesAndCities('STATE', { id_country: 1 });
    }, [fetchData, GetStatesAndCities]);

    const handleChangeCountryState = (e, action) => {
        const { name, value } = e.target;
        console.log({ name, value });
        if (action === 'STATE') {
            setLocationForm((prev) => ({ ...prev, [name]: value }));
            if (value === 'CO')
                GetStatesAndCities('STATE', { id_country: 1 });
        }
        if (action === 'CITY') {
            setLocationForm((prev) => ({ ...prev, [name]: value }));
            GetStatesAndCities('CITY', { code: value });
        }
    }

    const validateField = (name, value) => {
        if (value === null || value === undefined || (typeof value === "string" && value.trim() === '')) {
            handleErrorInput(name, { isError: true, messageError: t("validation.requiredField") });
            return false;
        } else {
            handleErrorInput(name, { isError: false, messageError: '' });
            return true;
        }
    };

    useImperativeHandle(ref, () => ({
        getData: () => dto.location,
        errorInputs,
    }));

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setLocationForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleAddBranch = () => {
        console.log("location ", locationForm)
        if (!ValidForm()) {
            sendNotification({
                msg: t("validation.requiredFieldsMissing"),
                variant: "error"
            })
            return false;
        }
        if (locationForm.edit) {
            console.log('locationForm ', locationForm);
            dto.location.splice(idxEdit, 1, locationForm);
            handleDirectInputChange('location', dto.location);
            updateOffice(userSession.token, locationForm);
            setIdxEdit(null);
        } else
            dto.location.push(locationForm);
        setLocationForm(initialState);
        sendNotification({
            msg: t("messages.saveError"),
            variant: "success"
        })
    };

    const ValidForm = () => {
        let hasError = true;
        Object.keys(locationForm).forEach((field) => {
            if (field === "id_supplier_office") return;
            const isValid = validateField(field, locationForm[field]);
            if (!isValid) hasError = isValid;
        });
        return hasError;
    };

    const handleEditBranch = (index) => {
        const branchToEdit = dto.location[index];
        branchToEdit.edit = true;
        setIdxEdit(index);
        setLocationForm(branchToEdit);
    };

    const handleDeleteBranch = (index) => {
        if (isEdit) {
            const id_supplier_office = dto.location[index]?.id_supplier_office
            deleteOffice(userSession.token, { id_supplier_office });
        }
        dto.location.splice(index, 1);
        handleDirectInputChange('location', dto.location);
    };

    return (
        <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="h6">{t("suppliers.officeLocations")}</Typography>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Tooltip title={t("add")}>
                        <IconButton
                            aria-label="close"
                            onClick={handleAddBranch}
                            sx={{ color: 'blue' }}
                        >
                            <AddTask />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Grid>

            <Grid size={{ xs: 4, sm: 4 }}>
                <FormControl
                    fullWidth
                    size='small'
                    error={Boolean(errorInputs.type_office?.isError)}
                    required
                >
                    <InputLabel id="address-type-label">{t("commonFields.location.addressType")}</InputLabel>
                    <Select
                        label={t("commonFields.location.addressType")}
                        name='type_office'
                        value={locationForm.type_office}
                        onChange={handleInputChange}
                    >
                        <MenuItem value="Oficina Principal">Oficina Principal</MenuItem>
                        <MenuItem value="Agencia">Agencia</MenuItem>
                        <MenuItem value="Sucursal">Sucursal</MenuItem>
                        <MenuItem value="Tienda">Tienda</MenuItem>
                    </Select>
                    {errorInputs.type_office?.isError && (
                        <FormHelperText>{errorInputs.type_office.messageError}</FormHelperText>
                    )}
                </FormControl>
            </Grid>

            <Grid size={{ xs: 4, sm: 4 }}>
                <TextField
                    fullWidth
                    size="small"
                    name='address'
                    label={t("commonFields.location.address")}
                    value={locationForm.address}
                    onChange={handleInputChange}
                    error={!!errorInputs.address?.isError}
                    helperText={errorInputs.address?.messageError}
                    required
                />
            </Grid>

            <Grid size={{ xs: 4, sm: 4 }}>
                <TextField
                    fullWidth
                    size="small"
                    label={t("commonFields.phone")}
                    name='phone'
                    type='text'
                    value={locationForm.phone}
                    onChange={(e) => {
                        const newValue = e.target.value.replace(/\D/g, '').slice(0, 10);
                        handleInputChange({ target: { name: 'phone', value: newValue } });
                    }}
                    inputProps={{
                        maxLength: 10,
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                    }}
                    error={!!errorInputs.phone?.isError}
                    helperText={errorInputs.phone?.messageError}
                    required
                />
            </Grid>

            <Grid size={{ xs: 4, sm: 4 }}>
                <TextField
                    fullWidth
                    size="small"
                    label={t("commonFields.cellphone")}
                    name='cellphone'
                    type='text'
                    value={locationForm.cellphone}
                    onChange={(e) => {
                        const newValue = e.target.value.replace(/\D/g, '').slice(0, 10);
                        handleInputChange({ target: { name: 'cellphone', value: newValue } });
                    }}
                    inputProps={{
                        maxLength: 10,
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                    }}
                    error={!!errorInputs.cellphone?.isError}
                    helperText={errorInputs.cellphone?.messageError}
                    required
                />
            </Grid>

            <Grid size={{ xs: 4, sm: 4 }}>
                <TextField
                    fullWidth
                    size="small"
                    name='email'
                    label={t("commonFields.email")}
                    value={locationForm.email}
                    onChange={handleInputChange}
                    error={!!errorInputs.email?.isError}
                    helperText={errorInputs.email?.messageError}
                    required
                />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
                <FormControl
                    fullWidth size="small"
                    error={Boolean(errorInputs.country?.isError)}
                    required
                >
                    <InputLabel>{t("commonFields.location.country")}</InputLabel>
                    <Select
                        label={t("commonFields.location.country")}
                        name="country"
                        value={locationForm.country}
                        onChange={(e) => handleChangeCountryState(e, 'STATE')}
                    >
                        {countries.map((option) => (
                            <MenuItem key={option.id_country} value={option.code}>
                                {option.name}
                            </MenuItem>
                        ))}
                    </Select>
                    {errorInputs.country?.isError && (
                        <FormHelperText>{errorInputs.country.messageError}</FormHelperText>
                    )}
                </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
                {locationForm.country === 'CO' ?
                    <FormControl
                        fullWidth size="small"
                        error={Boolean(errorInputs.state?.isError)}
                        required
                    >
                        <InputLabel>{t("commonFields.location.state")}</InputLabel>
                        <Select
                            label={t("commonFields.location.state")}
                            name="state"
                            value={locationForm.state}
                            onChange={(e) => handleChangeCountryState(e, 'CITY')}
                        >
                            {states.map((option) => (
                                <MenuItem key={option.id_state} value={option.code}>
                                    {option.name}
                                </MenuItem>
                            ))}
                        </Select>
                        {errorInputs.state?.isError && (
                            <FormHelperText>{errorInputs.state.messageError}</FormHelperText>
                        )}
                    </FormControl>
                    :
                    <TextField
                        label={t("commonFields.location.state")}
                        name="state"
                        value={locationForm.state}
                        onChange={handleInputChange}
                        fullWidth
                        size="small"
                        error={Boolean(errorInputs.state?.isError)}
                        helperText={errorInputs.state?.messageError || ''}
                        required
                    />
                }
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
                {locationForm.country === 'CO' ?
                    <FormControl
                        fullWidth size="small"
                        error={Boolean(errorInputs.city?.isError)}
                        required
                    >
                        <InputLabel>{t("commonFields.location.city")}</InputLabel>
                        <Select
                            label={t("commonFields.location.city")}
                            name="city"
                            value={locationForm.city}
                            onChange={handleInputChange}
                        >
                            {cities.map((option) => (
                                <MenuItem key={option.code} value={option.code}>
                                    {option.name}
                                </MenuItem>
                            ))}
                        </Select>
                        {errorInputs.city?.isError && (
                            <FormHelperText>{errorInputs.city.messageError}</FormHelperText>
                        )}
                    </FormControl>
                    :
                    <TextField
                        label={t("commonFields.location.city")}
                        name="city"
                        value={locationForm.city}
                        onChange={handleInputChange}
                        fullWidth
                        size="small"
                        error={Boolean(errorInputs.city?.isError)}
                        helperText={errorInputs.city?.messageError || ''}
                        required
                    />
                }
            </Grid>

            <Grid size={{ xs: 4, sm: 4 }}>
                <TextField
                    fullWidth
                    size="small"
                    label={t("commonFields.location.postalCode")}
                    name='postal_code'
                    value={locationForm.postal_code}
                    onChange={handleInputChange}
                    error={!!errorInputs.postal_code?.isError}
                    helperText={errorInputs.postal_code?.messageError}
                    required
                />
            </Grid>

            {dto.location.length > 0 && (
                <TableContainer component={Paper} sx={{ mt: 4 }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t("commonFields.location.addressType")}</TableCell>
                                <TableCell>{t("commonFields.location.address")}</TableCell>
                                <TableCell>{t("commonFields.phone")}</TableCell>
                                <TableCell>{t("commonFields.cellphone")}</TableCell>
                                <TableCell>{t("commonFields.email")}</TableCell>
                                <TableCell>{t("commonFields.location.country")}</TableCell>
                                <TableCell>{t("commonFields.location.state")}</TableCell>
                                <TableCell>{t("commonFields.location.city")}</TableCell>
                                <TableCell>{t("commonFields.location.postalCode")}</TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {dto.location.map((branch, index) => (
                                <TableRow key={index}>
                                    <TableCell>{branch.type_office}</TableCell>
                                    <TableCell>{branch.address}</TableCell>
                                    <TableCell>{branch.phone}</TableCell>
                                    <TableCell>{branch.cellphone}</TableCell>
                                    <TableCell>{branch.email}</TableCell>
                                    <TableCell>{branch.country}</TableCell>
                                    <TableCell>{branch.state}</TableCell>
                                    <TableCell>{branch.city}</TableCell>
                                    <TableCell>{branch.postal_code}</TableCell>
                                    <TableCell>
                                        <Tooltip title={t("edit")}>
                                            <IconButton
                                                color="primary"
                                                onClick={() => handleEditBranch(index)}
                                            >
                                                <Edit />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={t("delete")}>
                                            <IconButton
                                                color="error"
                                                onClick={() => handleDeleteBranch(index)}
                                            >
                                                <Delete />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Grid>
    );
});



